"use client";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";

import type { BookingArea } from "@/server-fns/booking/bookingAreas";

// A time to offer, with the seating area it is offered in when the store has area choice.
export type TimeSuggestion = { bookingStartTimeHHMM: string; area?: BookingArea };

type Props = {
    suggestions: TimeSuggestion[];
    onSelect: (time: string, area?: BookingArea) => void;
    onManualMode: (hasInput: boolean) => void;
    title?: string;
};

export const AISuggestions = ({
    suggestions,
    onSelect,
    onManualMode,
    title = "Nearby availability found! How about these?",
}: Props) => {
    if (suggestions.length === 0) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Box
                sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: "16px",
                    bgcolor: "rgba(25, 118, 210, 0.04)",
                    border: "1px dashed",
                    borderColor: "primary.light",
                    textAlign: "center",
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="center" gap={1} mb={1.5}>
                    <AutoAwesomeIcon sx={{ fontSize: 16, color: "primary.main" }} />
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                        {title}
                    </Typography>
                </Stack>

                <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1} mb={2}>
                    {suggestions.map(({ bookingStartTimeHHMM: time, area }) => (
                        <Chip
                            key={`${time}-${area?.id}`}
                            label={area ? `${time} · ${area.name}` : time}
                            onClick={() => (area ? onSelect(time, area) : onSelect(time))}
                            sx={{
                                borderRadius: "8px",
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "rgba(0,0,0,0.08)",
                                fontWeight: 600,
                                "&:hover": {
                                    bgcolor: "primary.main",
                                    color: "white",
                                    borderColor: "primary.main",
                                },
                                transition: "all 0.2s ease",
                            }}
                        />
                    ))}
                </Stack>

                <Button
                    size="small"
                    onClick={() => onManualMode(true)}
                    sx={{
                        textTransform: "none",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                    }}
                >
                    None of these work? Check manually
                </Button>
            </Box>
        </motion.div>
    );
};
